(() => {
  'use strict';

  const root = document.querySelector('[data-jv-shipping-root]');
  if (!root || root.dataset.jvShippingReady === 'true') return;
  root.dataset.jvShippingReady = 'true';

  const configNode = root.querySelector('[data-jv-shipping-config]');
  if (!configNode) return;

  let config;

  try {
    config = JSON.parse(configNode.textContent);
  } catch (error) {
    console.error('[JV Shipping] Configuração inválida.', error);
    return;
  }

  const prefix = config.storagePrefix || 'jv_shipping_v2';

  const keys = {
    location: `${prefix}:location`,
    quotes: `${prefix}:quotes`,
    lastQuote: `${prefix}:last_quote`
  };

  const DAY = 24 * 60 * 60 * 1000;
  const MINUTE = 60 * 1000;

  const state = {
    location: null,
    quote: null,
    cart: null,
    requestId: 0,
    selectedVariantId: null,
    autoInputTimer: null,
    cartRefreshTimer: null,
    previewRefreshTimer: null,
    expandProductOptionsOnNextRender: false,
    lastAutoSubmittedCep: ''
  };

  const parseJSON = (value, fallback = null) => {
    try {
      return JSON.parse(value);
    } catch (_) {
      return fallback;
    }
  };

  const storageGet = (key, fallback = null) => {
    try {
      return parseJSON(localStorage.getItem(key), fallback);
    } catch (_) {
      return fallback;
    }
  };

  const storageSet = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (_) {}
  };

  const storageRemove = (key) => {
    try {
      localStorage.removeItem(key);
    } catch (_) {}
  };

  const digits = (value) => String(value ?? '').replace(/\D/g, '');
  const normalizeCep = (value) => digits(value).slice(0, 8);

  const formatCep = (value) => {
    const cep = normalizeCep(value);
    return cep.length > 5 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : cep;
  };

  const parseMoney = (value) => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (value == null) return null;

    const text = String(value).trim().toLowerCase();

    if (text === 'grátis' || text === 'gratis') return 0;

    const normalized = text
      .replace(/[^\d,.-]/g, '')
      .replace(/\.(?=\d{3}(?:\D|$))/g, '')
      .replace(',', '.');

    const number = Number(normalized);
    return Number.isFinite(number) ? number : null;
  };

  const formatMoneyPlain = (value) => {
    const number = parseMoney(value);

    if (number === null) return 'Indisponível';
    if (number <= 0) return 'Grátis';

    return number.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const formatMoneyHtml = (value) => {
    const number = parseMoney(value);

    if (number === null) return 'Indisponível';
    if (number <= 0) {
      return '<span class="frete-gratis-badge">Grátis</span>';
    }

    return number.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const formatDays = (value) => {
    const number = Number(value);

    if (Number.isFinite(number)) {
      return number === 1 ? '1 dia útil' : `${number} dias úteis`;
    }

    return String(value ?? '').trim() || 'Prazo sob consulta';
  };

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const stripHtml = (value) => {
    const element = document.createElement('div');
    element.innerHTML = String(value ?? '');
    return (element.textContent || '').replace(/\s+/g, ' ').trim();
  };

  const sanitizeCarrierHtml = (value) => {
    const template = document.createElement('template');
    template.innerHTML = String(value ?? '');

    const sanitizeNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return escapeHtml(node.textContent || '');
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return '';

      if (
        node.tagName === 'SPAN' &&
        node.classList.contains('motoboy-badge')
      ) {
        return `<span class="motoboy-badge">${escapeHtml(node.textContent || '')}</span>`;
      }

      return Array.from(node.childNodes).map(sanitizeNode).join('');
    };

    return Array.from(template.content.childNodes)
      .map(sanitizeNode)
      .join('');
  };

  const dispatch = (name, detail) => {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  };

  const setStatus = (message = '', type = '') => {
    document
      .querySelectorAll('[data-jv-shipping-status]')
      .forEach((element) => {
        element.textContent = message;
        element.dataset.state = type;
      });
  };

  const setBusy = (busy) => {
    document
      .querySelectorAll(
        '[data-jv-shipping-submit], ' +
        '[data-jv-shipping-geolocate], ' +
        '[data-jv-shipping-cep-input]'
      )
      .forEach((element) => {
        element.disabled = busy;
      });

    document
      .querySelectorAll('[data-jv-shipping-skeleton]')
      .forEach((element) => {
        element.hidden = !busy;
      });

    if (!busy) return;

    /*
     * Durante o carregamento o campo e o botão continuam visíveis.
     * Escondemos apenas o resultado antigo para não misturar duas cotações.
     */
    document
      .querySelectorAll('[data-jv-product-shipping]')
      .forEach((widget) => {
        const entry = widget.querySelector('[data-jv-shipping-entry]');
        const compact = widget.querySelector('[data-jv-shipping-compact]');

        if (entry) entry.hidden = false;
        if (compact) compact.hidden = true;
      });

    document
      .querySelectorAll('[data-jv-shipping-results-container]')
      .forEach((element) => {
        if (!element.closest('[data-jv-product-shipping]')) {
          element.hidden = true;
        }
      });
  };

  const getSavedLocation = () => {
    const saved = storageGet(keys.location);

    if (!saved?.cep || !saved?.expiresAt) return null;

    if (Date.now() > Number(saved.expiresAt)) {
      storageRemove(keys.location);
      return null;
    }

    return saved;
  };

  const saveLocation = (location) => {
    const cep = normalizeCep(location?.cep);

    if (cep.length !== 8) {
      throw new Error('CEP inválido.');
    }

    const saved = {
      cep,
      city: String(location.city || location.localidade || '').trim(),
      state: String(location.state || location.uf || '').trim(),
      source: location.source || 'manual',
      updatedAt: Date.now(),
      expiresAt:
        Date.now() + Number(config.locationTtlDays || 180) * DAY
    };

    state.location = saved;
    storageSet(keys.location, saved);
    renderLocation();
    fillCepInputs();
    dispatch('jv:shipping-location-change', { location: saved });

    return saved;
  };

  const getQuotes = () => {
    const quotes = storageGet(keys.quotes, {});
    return quotes && typeof quotes === 'object' ? quotes : {};
  };

  const getCachedQuote = (cacheKey, allowExpired = false) => {
    const quote = getQuotes()[cacheKey];

    if (!quote) return null;

    if (
      !allowExpired &&
      Date.now() > Number(quote.expiresAt || 0)
    ) {
      return null;
    }

    return quote;
  };

  const saveQuote = (cacheKey, quote) => {
    const quotes = getQuotes();
    quotes[cacheKey] = quote;

    const newest = Object.entries(quotes)
      .sort(([, first], [, second]) => {
        return Number(second.createdAt || 0) - Number(first.createdAt || 0);
      })
      .slice(0, 16);

    storageSet(keys.quotes, Object.fromEntries(newest));
    storageSet(keys.lastQuote, quote);
  };

  const getLastQuote = () => {
    const quote = storageGet(keys.lastQuote);
    return quote?.cep === state.location?.cep ? quote : null;
  };

  const fillCepInputs = () => {
    if (!state.location) return;

    document
      .querySelectorAll('[data-jv-shipping-cep-input]')
      .forEach((input) => {
        input.value = formatCep(state.location.cep);
      });
  };

  const getLocationLabel = (location = state.location) => {
    if (!location) return 'Informe seu CEP';

    const city = String(location.city || '').trim();
    const stateCode = String(location.state || '').trim().toUpperCase();

    if (city && stateCode) return `${city} - ${stateCode}`;
    if (city) return city;

    return `CEP ${formatCep(location.cep)}`;
  };

  function renderLocation() {
    const text = getLocationLabel();

    document
      .querySelectorAll(
        '[data-jv-shipping-location], [data-jv-shipping-current-cep]'
      )
      .forEach((element) => {
        element.textContent = text;
      });
  }

  const getDaysNumber = (value) => {
    const directNumber = Number(value);

    if (Number.isFinite(directNumber)) return directNumber;

    const match = String(value ?? '').match(/\d+/);
    return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
  };

  const sortServicesByPriceAndTime = (services) => {
    return [...services].sort((first, second) => {
      const priceDifference = Number(first.value) - Number(second.value);

      if (priceDifference !== 0) return priceDifference;

      return getDaysNumber(first.days) - getDaysNumber(second.days);
    });
  };

  const normalizeTransportadoras = (payload) => {
    const list =
      payload?.transportadoras ||
      payload?.result?.transportadoras ||
      [];

    if (!Array.isArray(list)) return [];

    const services = list
      .map((frete, index) => ({
        id: `${index}-${frete?.transportadora || 'entrega'}`,
        carrierHtml: sanitizeCarrierHtml(
          frete?.transportadora || 'Entrega'
        ),
        carrierPlain: stripHtml(
          frete?.transportadora || 'Entrega'
        ),
        days: frete?.prazo,
        value: parseMoney(frete?.valor),
        freeByThreshold: false
      }))
      .filter((frete) => frete.value !== null);

    return sortServicesByPriceAndTime(services);
  };

  const applyFreeShippingPolicy = (services, context) => {
    const threshold = Number(config.freeShippingThreshold || 249);
    const orderedServices = sortServicesByPriceAndTime(services);

    if (
      !orderedServices.length ||
      !context ||
      Number(context.price) < threshold
    ) {
      return orderedServices;
    }

    const shortestService = [...orderedServices].sort(
      (first, second) => {
        const timeDifference =
          getDaysNumber(first.days) - getDaysNumber(second.days);

        if (timeDifference !== 0) return timeDifference;

        return Number(first.value) - Number(second.value);
      }
    )[0];

    const freeService = {
      ...shortestService,
      value: 0,
      freeByThreshold: true
    };

    return [
      freeService,
      ...orderedServices.filter(
        (service) => service.id !== shortestService.id
      )
    ];
  };

  const renderFeaturedService = (service) => {
    if (!service) return '';

    return `
      <div class="jv-shipping-featured__left">
        <div class="frete-info">
          <span class="frete-transportadora">${service.carrierHtml}</span>
          <span class="frete-prazo">${escapeHtml(formatDays(service.days))}</span>
        </div>
      </div>

      <span class="frete-preco">${formatMoneyHtml(service.value)}</span>
    `;
  };

  const renderAlternativeService = (service) => {
    return `
      <div class="frete-item">
        <div class="frete-info">
          <span class="frete-transportadora">${service.carrierHtml}</span>
          <span class="frete-prazo">${escapeHtml(formatDays(service.days))}</span>
        </div>

        <span class="frete-preco">${formatMoneyHtml(service.value)}</span>
      </div>
    `;
  };

  const showProductEntry = (widget) => {
    const entry = widget.querySelector('[data-jv-shipping-entry]');
    const compact = widget.querySelector('[data-jv-shipping-compact]');
    const skeleton = widget.querySelector('[data-jv-shipping-skeleton]');

    if (entry) entry.hidden = false;
    if (compact) compact.hidden = true;
    if (skeleton) skeleton.hidden = true;
  };

  const renderProductShippingWidgets = (quote) => {
    const services = Array.isArray(quote?.services)
      ? quote.services
      : [];

    const shouldExpand = state.expandProductOptionsOnNextRender === true;

    document
      .querySelectorAll('[data-jv-product-shipping]')
      .forEach((widget) => {
        const entry = widget.querySelector('[data-jv-shipping-entry]');
        const compact = widget.querySelector('[data-jv-shipping-compact]');
        const skeleton = widget.querySelector('[data-jv-shipping-skeleton]');
        const currentLocation = widget.querySelector(
          '[data-jv-shipping-current-cep]'
        );
        const featured = widget.querySelector(
          '[data-jv-shipping-featured]'
        );
        const toggle = widget.querySelector('[data-jv-shipping-toggle]');
        const toggleLabel = widget.querySelector(
          '[data-jv-shipping-toggle-label]'
        );
        const resultsContainer = widget.querySelector(
          '[data-jv-shipping-results-container]'
        );
        const results = widget.querySelector(
          '[data-jv-shipping-results]'
        );

        if (!state.location || !services.length) {
          showProductEntry(widget);
          return;
        }

        const primary = services[0];
        const alternatives = services.slice(1);

        if (entry) entry.hidden = true;
        if (compact) compact.hidden = false;
        if (skeleton) skeleton.hidden = true;

        if (currentLocation) {
          currentLocation.textContent = getLocationLabel(state.location);
        }

        if (featured) {
          featured.innerHTML = renderFeaturedService(primary);
        }

        if (results) {
          results.innerHTML = alternatives
            .map(renderAlternativeService)
            .join('');
        }

        if (!toggle || !toggleLabel || !resultsContainer) return;

        if (!alternatives.length) {
          toggle.hidden = true;
          resultsContainer.hidden = true;
          return;
        }

        toggle.hidden = false;
        toggle.setAttribute(
          'aria-expanded',
          shouldExpand ? 'true' : 'false'
        );
        resultsContainer.hidden = !shouldExpand;
        toggleLabel.textContent = shouldExpand
          ? 'Ocultar'
          : alternatives.length === 1
            ? 'Mais 1 opção'
            : `Mais ${alternatives.length} opções`;
      });

    /* A abertura automática acontece só no cálculo feito nesta página. */
    state.expandProductOptionsOnNextRender = false;
  };

  function renderQuote(quote) {
    state.quote = quote || null;
    const cheapest = quote?.cheapest || null;

    document
      .querySelectorAll('[data-jv-shipping-service]')
      .forEach((element) => {
        element.textContent = cheapest?.carrierPlain || 'Entrega';
      });

    document
      .querySelectorAll('[data-jv-shipping-summary]')
      .forEach((element) => {
        element.textContent = cheapest
          ? `${formatDays(cheapest.days)} · ${formatMoneyPlain(cheapest.value)}`
          : state.location
            ? 'Ver opções e prazos'
            : 'Ver opções de entrega';
      });

    renderResults(quote);
    renderProductShippingWidgets(quote);
  }

  function renderResults(quote) {
    const services = Array.isArray(quote?.services)
      ? quote.services
      : [];

    document
      .querySelectorAll('[data-jv-shipping-results]')
      .forEach((results) => {
        if (results.closest('[data-jv-product-shipping]')) return;

        const container = results.closest(
          '[data-jv-shipping-results-container]'
        );

        if (!container) return;

        if (!services.length) {
          container.hidden = true;
          results.innerHTML = '';
          return;
        }

        results.innerHTML = services
          .map((frete) => `
            <div class="frete-item">
              <div class="frete-info">
                <span class="frete-transportadora">${frete.carrierHtml}</span>
                <span class="frete-prazo">${escapeHtml(formatDays(frete.days))}</span>
              </div>

              <span class="frete-preco">${formatMoneyHtml(frete.value)}</span>
            </div>
          `)
          .join('');

        container.hidden = false;
      });
  }

  const getShopRoot = () => window.Shopify?.routes?.root || '/';

  const fetchCart = async () => {
    try {
      const response = await fetch(`${getShopRoot()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return null;

      const cart = await response.json();
      state.cart = cart;

      return cart;
    } catch (_) {
      return null;
    }
  };

  const getCartSignature = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];

    if (!items.length) return 'empty';

    return items
      .map((item) => `${item.variant_id}x${item.quantity}`)
      .sort()
      .join(',');
  };

  const createCartContext = (cart) => {
    if (!cart) return null;

    return {
      type: 'cart',
      key: `cart:${getCartSignature(cart)}`,
      price: Number(cart.total_price || 0) / 100,
      itemCount: Number(cart.item_count || 0)
    };
  };

  const getCurrentContext = async (providedCart = null) => {
    const cart = providedCart || await fetchCart();
    return createCartContext(cart);
  };

  const getProductPreviewContext = () => {
    if (!config.product) return null;

    const productForms = Array.from(
      document.querySelectorAll('form[action*="/cart/add"]')
    );

    const visibleForm = productForms.find((form) => {
      return form.offsetParent !== null;
    });

    const form = visibleForm || productForms[0] || null;

    const variantInput =
      form?.querySelector('input[name="id"], select[name="id"]') ||
      document.querySelector('select[name="id"]');

    const variantId = String(
      variantInput?.value ||
      config.product.selectedVariantId ||
      ''
    );

    const configuredPrice = config.product.variantPrices?.[variantId];

    const price = configuredPrice != null
      ? Number(configuredPrice)
      : Number(config.product.selectedVariantPrice);

    if (!Number.isFinite(price)) return null;

    const quantityInput =
      form?.querySelector('input[name="quantity"]') ||
      document.querySelector('input[name="quantity"]');

    const quantityValue = Number(quantityInput?.value || 1);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0
      ? Math.floor(quantityValue)
      : 1;

    state.selectedVariantId = variantId;

    return {
      form,
      variantId,
      price,
      quantity,
      total: price * quantity
    };
  };

  const getFreeShippingThreshold = () => {
    const threshold = Number(config.freeShippingThreshold || 249);
    return Number.isFinite(threshold) && threshold > 0
      ? threshold
      : 249;
  };

  const setWidth = (element, value) => {
    if (!element) return;

    const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
    element.style.width = `${safeValue}%`;
  };

  const renderFreeShippingProgress = (cart = state.cart) => {
    const threshold = getFreeShippingThreshold();
    const itemCount = Number(cart?.item_count || 0);
    const cartTotal = Number(cart?.total_price || 0) / 100;
    const product = getProductPreviewContext();
    const productTotal = Number(product?.total || 0);
    const projectedTotal = cartTotal + productTotal;
    const canUnlockWithProduct =
      itemCount > 0 &&
      cartTotal < threshold &&
      productTotal > 0 &&
      projectedTotal >= threshold;

    const currentPercent = Math.min(
      100,
      Math.max(0, (cartTotal / threshold) * 100)
    );

    const previewPercent = canUnlockWithProduct
      ? Math.max(0, 100 - currentPercent)
      : 0;

    const remaining = Math.max(0, threshold - cartTotal);

    document
      .querySelectorAll('[data-jv-free-shipping]')
      .forEach((widget) => {
        const title = widget.querySelector(
          '[data-jv-free-shipping-title]'
        );
        const goal = widget.querySelector(
          '[data-jv-free-shipping-goal]'
        );
        const detail = widget.querySelector(
          '[data-jv-free-shipping-detail]'
        );
        const track = widget.querySelector(
          '[data-jv-free-shipping-track]'
        );
        const current = widget.querySelector(
          '[data-jv-free-shipping-current]'
        );
        const preview = widget.querySelector(
          '[data-jv-free-shipping-preview]'
        );

        /* Sem itens no carrinho, a barra não aparece. */
        if (itemCount <= 0) {
          widget.hidden = true;
          setWidth(current, 0);
          setWidth(preview, 0);
          if (detail) detail.hidden = true;
          return;
        }

        widget.hidden = false;

        if (goal) {
          goal.textContent = formatMoneyPlain(threshold)
            .replace(',00', '');
        }

        if (track) {
          track.setAttribute('aria-valuemax', String(threshold));
          track.setAttribute(
            'aria-valuenow',
            String(Math.min(cartTotal, threshold).toFixed(2))
          );
        }

        setWidth(current, currentPercent);

        if (preview) {
          preview.style.left = `${currentPercent}%`;
          setWidth(preview, previewPercent);
        }

        if (!title || !detail) return;

        if (cartTotal >= threshold) {
          title.innerHTML = 'Você ganhou <strong>frete grátis</strong>';
          detail.textContent = '';
          detail.hidden = true;
          return;
        }

        if (canUnlockWithProduct) {
          title.innerHTML = 'Este produto libera seu <strong>frete grátis</strong>';
          detail.textContent =
            'Compre também esse produto pra ganhar frete grátis';
          detail.hidden = false;
          return;
        }

        const remainingText = escapeHtml(formatMoneyPlain(remaining));
        const prefixText = remaining <= 100 ? 'Faltam só ' : 'Faltam ';

        title.innerHTML =
          `${prefixText}<strong>${remainingText}</strong> ` +
          'para o <strong>frete grátis</strong>';
        detail.textContent = '';
        detail.hidden = true;
      });
  };

  const refreshFreeShippingProgress = async ({ cart = null } = {}) => {
    const currentCart = cart || await fetchCart();
    renderFreeShippingProgress(currentCart || state.cart);
    return currentCart;
  };

  const makeCacheKey = (location, context) => [
    location.cep,
    context.key,
    Number(context.price || 0).toFixed(2)
  ].join('|');

  const requestQuote = async ({
    force = false,
    silent = false,
    cart = null
  } = {}) => {
    if (!state.location || !config.quoteEndpoint) {
      if (!silent) setBusy(false);
      return null;
    }

    const context = await getCurrentContext(cart);

    if (!context) {
      const lastQuote = getLastQuote();

      if (lastQuote) renderQuote(lastQuote);

      setStatus('');
      if (!silent) setBusy(false);
      return lastQuote;
    }

    renderFreeShippingProgress(state.cart);

    const cacheKey = makeCacheKey(state.location, context);

    /*
     * IMPORTANTE:
     * usamos até a cotação expirada enquanto o CEP e o carrinho forem iguais.
     * Assim, trocar de produto ou variante não dispara uma nova requisição.
     * A cotação só é forçada ao trocar o CEP ou quando o carrinho muda.
     */
    const cached = getCachedQuote(cacheKey, true);

    if (cached && !force) {
      renderQuote(cached);
      setStatus('');
      dispatch('jv:shipping-updated', cached);
      return cached;
    }

    const currentRequestId = ++state.requestId;

    if (!silent) {
      setStatus('');
      setBusy(true);
    }

    try {
      const response = await fetch(config.quoteEndpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cep: state.location.cep,
          price: context.price
        })
      });

      const result = await response.json().catch(() => ({}));

      if (currentRequestId !== state.requestId) return null;

      if (!response.ok) {
        throw new Error(
          result?.error ||
          result?.message ||
          'Não foi possível calcular o frete.'
        );
      }

      const normalizedServices = normalizeTransportadoras(result);

      if (!normalizedServices.length) {
        throw new Error(
          'Nenhuma opção de entrega foi encontrada para esse CEP.'
        );
      }

      const services = applyFreeShippingPolicy(
        normalizedServices,
        context
      );

      const quote = {
        cep: state.location.cep,
        context,
        services,
        cheapest: services[0],
        createdAt: Date.now(),
        expiresAt:
          Date.now() +
          Number(config.quoteTtlMinutes || 360) * MINUTE
      };

      saveQuote(cacheKey, quote);
      renderQuote(quote);
      setStatus('');
      dispatch('jv:shipping-updated', quote);

      return quote;
    } catch (error) {
      console.error('[JV Shipping]', error);
      setStatus(
        error?.message || 'Erro ao calcular o frete.',
        'error'
      );
      dispatch('jv:shipping-error', { error });

      const lastQuote = getLastQuote();
      if (lastQuote) renderQuote(lastQuote);

      return null;
    } finally {
      if (!silent && currentRequestId === state.requestId) {
        setBusy(false);
      }
    }
  };

  const lookupCepLocation = async (cep) => {
    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${encodeURIComponent(cep)}/json/`,
        {
          headers: { Accept: 'application/json' }
        }
      );

      if (!response.ok) return null;

      const result = await response.json().catch(() => null);

      if (!result || result.erro) return null;

      const city = String(result.localidade || '').trim();
      const stateCode = String(result.uf || '').trim();

      if (!city && !stateCode) return null;

      return {
        cep,
        city,
        state: stateCode,
        source: 'manual'
      };
    } catch (_) {
      /* A cotação continua normalmente e usa o CEP como fallback. */
      return null;
    }
  };

  const submitCep = async (value) => {
    clearTimeout(state.autoInputTimer);

    const cep = normalizeCep(value);

    if (cep.length !== 8) {
      setStatus('CEP inválido. Digite os 8 números.', 'error');
      document
        .querySelector('[data-jv-shipping-cep-input]')
        ?.focus();
      return null;
    }

    /* O skeleton aparece antes de qualquer consulta ao carrinho ou CEP. */
    setStatus('');
    setBusy(true);
    state.expandProductOptionsOnNextRender = true;
    saveLocation({ cep, source: 'manual' });

    /* Busca cidade/UF em paralelo, sem atrasar a cotação. */
    lookupCepLocation(cep).then((location) => {
      if (location && state.location?.cep === cep) {
        saveLocation(location);
      }
    });

    /* Trocar o CEP sempre força uma nova cotação. */
    return requestQuote({ force: true });
  };

  const reverseGeocode = async (latitude, longitude) => {
    if (!config.reverseEndpoint) {
      throw new Error(
        'A busca pela localização ainda não foi configurada.'
      );
    }

    const response = await fetch(config.reverseEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ latitude, longitude })
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        result?.error ||
        'Não foi possível descobrir o CEP pela localização.'
      );
    }

    const cep = normalizeCep(
      result?.cep ||
      result?.postcode ||
      result?.postal_code
    );

    if (cep.length !== 8) {
      throw new Error(
        'Encontramos a região, mas não conseguimos identificar um CEP válido.'
      );
    }

    return {
      cep,
      city: result?.city || result?.localidade || '',
      state: result?.state || result?.uf || '',
      source: 'geolocation'
    };
  };

  const useGeolocation = async ({ silent = false } = {}) => {
    if (!navigator.geolocation) {
      if (!silent) {
        setStatus(
          'Seu navegador não oferece localização automática.',
          'error'
        );
      }

      return null;
    }

    if (!silent) {
      setStatus('');
      setBusy(true);
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const location = await reverseGeocode(
              position.coords.latitude,
              position.coords.longitude
            );

            saveLocation(location);
            state.expandProductOptionsOnNextRender = true;

            /* Uma nova localização sempre força uma cotação. */
            await requestQuote({ force: true, silent });
            resolve(location);
          } catch (error) {
            setStatus(
              `${error.message} Você ainda pode digitar o CEP.`,
              'error'
            );
            resolve(null);
          } finally {
            if (!silent) setBusy(false);
          }
        },
        (error) => {
          if (!silent) {
            const message = error.code === 1
              ? 'A localização não foi permitida. Digite seu CEP para continuar.'
              : 'Não conseguimos acessar sua localização. Digite seu CEP para continuar.';

            setStatus(message, 'error');
            setBusy(false);
          }

          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 9000,
          maximumAge: 30 * MINUTE
        }
      );
    });
  };

  const openModal = () => {
    const modal = root.querySelector('[data-jv-shipping-modal]');
    if (!modal) return;

    modal.hidden = false;
    root.classList.add('jv-shipping-global--modal-open');
    document.body.classList.add('jv-shipping-modal-open');
    fillCepInputs();

    setTimeout(() => {
      modal
        .querySelector('[data-jv-shipping-cep-input]')
        ?.focus();
    }, 30);
  };

  const closeModal = () => {
    const modal = root.querySelector('[data-jv-shipping-modal]');
    if (!modal) return;

    modal.hidden = true;
    root.classList.remove('jv-shipping-global--modal-open');
    document.body.classList.remove('jv-shipping-modal-open');
  };

  const tryAutomaticLocation = async () => {
    if (
      state.location ||
      !navigator.permissions ||
      !navigator.geolocation
    ) {
      return;
    }

    try {
      const permission = await navigator.permissions.query({
        name: 'geolocation'
      });

      if (permission.state === 'granted') {
        await useGeolocation({ silent: true });
      }
    } catch (_) {}
  };

  const schedulePreviewRefresh = () => {
    clearTimeout(state.previewRefreshTimer);

    state.previewRefreshTimer = setTimeout(() => {
      renderFreeShippingProgress(state.cart);
    }, 90);
  };

  const scheduleCartRefresh = ({ forceQuote = true } = {}) => {
    clearTimeout(state.cartRefreshTimer);

    state.cartRefreshTimer = setTimeout(async () => {
      const cart = await fetchCart();

      renderFreeShippingProgress(cart || state.cart);

      if (state.location && forceQuote && cart) {
        const nextContext = createCartContext(cart);
        const quotedContext = state.quote?.context || null;
        const quoteAlreadyMatchesCart = Boolean(
          nextContext &&
          quotedContext &&
          nextContext.key === quotedContext.key &&
          Number(nextContext.price || 0).toFixed(2) ===
            Number(quotedContext.price || 0).toFixed(2)
        );

        /*
         * O carrinho mudou: esta é uma das poucas situações em que
         * a cotação deve ser realmente refeita. A comparação acima
         * evita duas chamadas quando o tema dispara mais de um evento
         * para a mesma adição.
         */
        if (!quoteAlreadyMatchesCart) {
          await requestQuote({
            force: true,
            silent: true,
            cart
          });
        }
      }
    }, 180);
  };

  /* O widget não interfere no botão de compra da PDP. */

  document.addEventListener('click', (event) => {
    const submitButton = event.target.closest(
      '[data-jv-shipping-submit]'
    );

    if (submitButton) {
      event.preventDefault();
      event.stopPropagation();

      const form = submitButton.closest('[data-jv-shipping-form]');
      const input = form?.querySelector(
        '[data-jv-shipping-cep-input]'
      );

      submitCep(input?.value || '');
      return;
    }

    const changeCepButton = event.target.closest(
      '[data-jv-shipping-change-cep]'
    );

    if (changeCepButton) {
      event.preventDefault();

      const widget = changeCepButton.closest(
        '[data-jv-product-shipping]'
      );

      if (widget) {
        showProductEntry(widget);
        fillCepInputs();

        setTimeout(() => {
          const input = widget.querySelector(
            '[data-jv-shipping-cep-input]'
          );
          input?.focus();
          input?.select();
        }, 20);
      }

      return;
    }

    const toggle = event.target.closest('[data-jv-shipping-toggle]');

    if (toggle) {
      event.preventDefault();

      const widget = toggle.closest('[data-jv-product-shipping]');
      const container = widget?.querySelector(
        '[data-jv-shipping-results-container]'
      );
      const label = toggle.querySelector(
        '[data-jv-shipping-toggle-label]'
      );
      const results = widget?.querySelector(
        '[data-jv-shipping-results]'
      );

      if (!container || !label || !results) return;

      const isOpen = toggle.getAttribute('aria-expanded') === 'true';

      toggle.setAttribute(
        'aria-expanded',
        isOpen ? 'false' : 'true'
      );
      container.hidden = isOpen;

      if (isOpen) {
        const count = results.children.length;
        label.textContent = count === 1
          ? 'Mais 1 opção'
          : `Mais ${count} opções`;
      } else {
        label.textContent = 'Ocultar';
      }

      return;
    }

    if (event.target.closest('[data-jv-shipping-open]')) {
      openModal();
      return;
    }

    if (event.target.closest('[data-jv-shipping-close]')) {
      closeModal();
      return;
    }

    if (event.target.closest('[data-jv-shipping-geolocate]')) {
      useGeolocation();
    }
  });

  document.addEventListener('submit', (event) => {
    const shippingForm = event.target.closest(
      '[data-jv-shipping-form]'
    );

    if (shippingForm) {
      event.preventDefault();

      submitCep(
        shippingForm.querySelector(
          '[data-jv-shipping-cep-input]'
        )?.value || ''
      );

      return;
    }

    /*
     * Fallback para temas que adicionam o produto sem usar fetch.
     * A espera dá tempo para o carrinho ser atualizado.
     */
    if (event.target.matches('form[action*="/cart/add"]')) {
      window.setTimeout(() => {
        scheduleCartRefresh({ forceQuote: true });
      }, 650);
    }
  });

  document.addEventListener('input', (event) => {
    const cepInput = event.target.closest(
      '[data-jv-shipping-cep-input]'
    );

    if (cepInput) {
      cepInput.value = formatCep(cepInput.value);
      setStatus('');

      const cep = normalizeCep(cepInput.value);

      document
        .querySelectorAll('[data-jv-shipping-cep-input]')
        .forEach((otherInput) => {
          if (otherInput !== cepInput) {
            otherInput.value = formatCep(cep);
          }
        });

      clearTimeout(state.autoInputTimer);

      if (cep.length < 8) {
        state.lastAutoSubmittedCep = '';
      }

      if (
        cep.length === 8 &&
        cep !== state.lastAutoSubmittedCep
      ) {
        state.lastAutoSubmittedCep = cep;
        submitCep(cep);
      }

      return;
    }

    if (event.target.matches('input[name="quantity"]')) {
      schedulePreviewRefresh();
    }
  });

  document.addEventListener('change', (event) => {
    if (
      event.target.matches(
        'form[action*="/cart/add"] [name="id"], ' +
        'select[name="id"], ' +
        'input[name="quantity"]'
      )
    ) {
      /*
       * Trocar variante ou quantidade só muda a simulação da barra.
       * Não faz uma nova cotação.
       */
      schedulePreviewRefresh();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeModal();
  });

  [
    'product:variant-change',
    'variant:change'
  ].forEach((eventName) => {
    document.addEventListener(eventName, schedulePreviewRefresh);
    window.addEventListener(eventName, schedulePreviewRefresh);
  });

  [
    'cart:updated',
    'cart:change',
    'cart:refresh',
    'shopify:cart:lines-update'
  ].forEach((eventName) => {
    document.addEventListener(eventName, () => {
      scheduleCartRefresh({ forceQuote: true });
    });

    window.addEventListener(eventName, () => {
      scheduleCartRefresh({ forceQuote: true });
    });
  });

  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (...argumentsList) => {
    const response = await nativeFetch(...argumentsList);

    try {
      const request = argumentsList[0];
      const requestUrl = typeof request === 'string'
        ? request
        : request?.url || '';

      const isCartMutation = /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/i
        .test(requestUrl);

      if (isCartMutation) {
        window.setTimeout(() => {
          scheduleCartRefresh({ forceQuote: true });
        }, 120);
      }
    } catch (_) {}

    return response;
  };

  window.JVShipping = {
    open: openModal,
    close: closeModal,
    setCep: submitCep,
    refresh: () => requestQuote({ force: true }),
    refreshCart: () => scheduleCartRefresh({ forceQuote: true }),
    refreshProgress: () => refreshFreeShippingProgress(),
    getLocation: () => state.location,
    getQuote: () => state.quote,
    getCart: () => state.cart,
    clear: () => {
      storageRemove(keys.location);
      storageRemove(keys.quotes);
      storageRemove(keys.lastQuote);

      state.location = null;
      state.quote = null;

      document
        .querySelectorAll('[data-jv-shipping-cep-input]')
        .forEach((input) => {
          input.value = '';
        });

      document
        .querySelectorAll('[data-jv-product-shipping]')
        .forEach(showProductEntry);

      renderLocation();
      renderQuote(null);
      renderFreeShippingProgress(state.cart);
      setStatus('');
    }
  };

  state.location = getSavedLocation();
  renderLocation();
  fillCepInputs();

  const lastQuote = getLastQuote();
  if (lastQuote) renderQuote(lastQuote);

  if (state.location) {
    /*
     * Na troca de página, a função procura a cotação do mesmo CEP+carrinho.
     * Se existir, reutiliza sem chamar novamente o endpoint.
     */
    requestQuote({ silent: true });
  } else {
    refreshFreeShippingProgress();
    tryAutomaticLocation();
  }
})();